import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '@/shared/components/Layout/Layout';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { Spinner } from '@/shared/components/ui/Spinner/Spinner';

// Lazy loaded components
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then(module => ({ default: module.RegisterPage })));
const OAuthCallbackPage = lazy(() => import('@/pages/auth/OAuthCallbackPage').then(module => ({ default: module.OAuthCallbackPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })));
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage').then(module => ({ default: module.ProjectsPage })));
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage').then(module => ({ default: module.ProjectDetailPage })));
const AddProjectPage = lazy(() => import('@/pages/projects/AddProjectPage').then(module => ({ default: module.AddProjectPage })));
const ChatPage = lazy(() => import('@/pages/chat/ChatPage').then(module => ({ default: module.ChatPage })));
const InboxPage = lazy(() => import('@/pages/inbox/InboxPage').then(module => ({ default: module.InboxPage })));
const GoalsPage = lazy(() => import('@/pages/goals/GoalsPage').then(module => ({ default: module.GoalsPage })));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage').then(module => ({ default: module.OnboardingPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(module => ({ default: module.SettingsPage })));
const ConnectCallbackPage = lazy(() => import('@/pages/settings/ConnectCallbackPage').then(module => ({ default: module.ConnectCallbackPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/billing/PaymentSuccessPage').then(module => ({ default: module.PaymentSuccessPage })));
const DigestPage = lazy(() => import('@/pages/digest/DigestPage').then(module => ({ default: module.DigestPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })));
const SourceOAuthCallbackPage = lazy(() => import('@/pages/sources/OAuthCallbackPage').then(module => ({ default: module.SourceOAuthCallbackPage })));
const MCPCallbackPage = lazy(() => import('@/pages/sources/MCPCallbackPage').then(module => ({ default: module.MCPCallbackPage })));
const AliceAuthorizePage = lazy(() => import('@/pages/alice/AliceAuthorizePage').then(module => ({ default: module.AliceAuthorizePage })));
const TeamPage = lazy(() => import('@/pages/settings/TeamPage').then(module => ({ default: module.TeamPage })));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage').then(module => ({ default: module.BillingPage })));
const AcceptInvitePage = lazy(() => import('@/pages/invites/AcceptInvitePage').then(module => ({ default: module.AcceptInvitePage })));
const AuditPage = lazy(() => import('@/pages/direct/AuditPage').then(module => ({ default: module.AuditPage })));
const ActionsPage = lazy(() => import('@/pages/direct/ActionsPage').then(module => ({ default: module.ActionsPage })));
const JournalPage = lazy(() => import('@/pages/direct/JournalPage').then(module => ({ default: module.JournalPage })));
const AgencyPage = lazy(() => import('@/pages/direct/AgencyPage').then(module => ({ default: module.AgencyPage })));

const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Spinner size="lg" className="text-blue-600" />
  </div>
);

/**
 * Application router with RBAC-protected routes.
 *
 * Route protection levels:
 * - Public: No authentication required (login, register, etc.)
 * - Protected: Authentication required, no permission check
 * - Permission-based: Requires specific permission (billing, chat, etc.)
 * - Role-based: Requires specific role (owner-only features)
 *
 * IMPORTANT: Frontend route guards are for UX only.
 * Backend enforces all permissions (OWASP A01:2021).
 */
export const router = createBrowserRouter([
  // Root redirect
  {
    path: '/',
    element: <Navigate to="/audit" replace />,
  },

  // ========================================
  // PUBLIC ROUTES - No authentication
  // ========================================
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <OAuthCallbackPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  // Source OAuth callback - handles redirect from Yandex OAuth
  {
    path: '/sources/oauth/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <SourceOAuthCallbackPage />
      </Suspense>
    ),
  },
  // Возврат с чужого MCP-сервера
  {
    path: '/sources/mcp/callback',
    element: (
      <Suspense fallback={<PageLoader />}>
        <MCPCallbackPage />
      </Suspense>
    ),
  },
  // Alice Account Linking authorization page
  {
    path: '/alice/authorize',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AliceAuthorizePage />
      </Suspense>
    ),
  },
  // Accept project invite - works for both authenticated and unauthenticated users
  {
    path: '/invites/:token/accept',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AcceptInvitePage />
      </Suspense>
    ),
  },

  // ========================================
  // PROTECTED ROUTES - Authentication required
  // ========================================
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      // Dashboard redirect for old links
      {
        path: 'dashboard',
        element: <Navigate to="/audit" replace />,
      },

      // Audit - main dashboard (спека 0139)
      {
        path: 'audit',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AuditPage />
          </Suspense>
        ),
      },

      // Actions - queue of proposed Direct fixes
      // Read (список правок) охраняется как на сервере — view_analytics;
      // сами кнопки записи гасятся в ActionsPage до manage_sources.
      {
        path: 'actions',
        element: (
          <ProtectedRoute permission="view_analytics" redirectTo="/audit">
            <Suspense fallback={<PageLoader />}>
              <ActionsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Journal - applied changes log with undo
      {
        path: 'journal',
        element: (
          <ProtectedRoute permission="view_analytics" redirectTo="/audit">
            <Suspense fallback={<PageLoader />}>
              <JournalPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Agency - cross-project overview (спека 0142)
      {
        path: 'agency',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AgencyPage />
          </Suspense>
        ),
      },

      // Inbox - legacy dashboard, still reachable directly
      {
        path: 'inbox',
        element: (
          <Suspense fallback={<PageLoader />}>
            <InboxPage />
          </Suspense>
        ),
      },

      // Goals - goal tracking
      {
        path: 'goals',
        element: (
          <Suspense fallback={<PageLoader />}>
            <GoalsPage />
          </Suspense>
        ),
      },

      // Onboarding - per-project setup
      {
        path: 'onboarding/:projectId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OnboardingPage />
          </Suspense>
        ),
      },

      // Projects - all authenticated users can view
      {
        path: 'projects',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectsPage />
          </Suspense>
        ),
      },
      {
        path: 'projects/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProjectDetailPage />
          </Suspense>
        ),
      },

      // Add project - admin or above
      {
        path: 'projects/new',
        element: (
          <ProtectedRoute anyRole={['owner', 'admin']} redirectTo="/projects">
            <Suspense fallback={<PageLoader />}>
              <AddProjectPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Chat - analyst or above (use_chat permission)
      {
        path: 'chat',
        element: (
          <ProtectedRoute permission="use_chat" redirectTo="/projects">
            <Suspense fallback={<PageLoader />}>
              <ChatPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'chat/:sessionId',
        element: (
          <ProtectedRoute permission="use_chat" redirectTo="/projects">
            <Suspense fallback={<PageLoader />}>
              <ChatPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Settings - all authenticated users
      {
        path: 'settings',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'settings/team',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TeamPage />
          </Suspense>
        ),
      },
      {
        path: 'settings/connect-callback',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ConnectCallbackPage />
          </Suspense>
        ),
      },

      // Billing - accessible to all authenticated users (non-owners see "contact admin" message)
      {
        path: 'billing',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BillingPage />
          </Suspense>
        ),
      },
      {
        path: 'billing/success',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PaymentSuccessPage />
          </Suspense>
        ),
      },

      // Digest - analyst or above (manage_digests permission)
      {
        path: 'digest',
        element: (
          <ProtectedRoute permission="manage_digests" redirectTo="/projects">
            <Suspense fallback={<PageLoader />}>
              <DigestPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Integrations - redirect to projects (integrations now managed per-project via Sources)
      {
        path: 'integrations',
        element: <Navigate to="/projects" replace />,
      },
    ],
  },

  // 404 - Not found
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);